/**
 * The Home panel — house, trade chooser, workshop bench.
 *
 * One slice of the base-view stylesheet. styles/index.ts concatenates every
 * slice into the single sheet injectStyles mounts.
 *
 * NO BACKTICKS anywhere in this file, comments included: the export below is
 * one template literal and a stray backtick terminates it. That has broken the
 * build twice, both times with esbuild pointing ten rows away from the cause.
 * Write class names in plain text.
 */
export default `
/* ─── Scrim + shell ───────────────────────────────────────────────────────
   z-index 1001, the same layer every other full-screen panel uses. .bv-hud
   sits at 1002 and stays readable and clickable above it, which is what makes
   the action bar able to close the panel it opened. The stage is a column of
   [title, plate]: the title sits above the plate over the scrim, the way the
   shop and the journal do it. */
.bv-home-scrim {
  position: fixed; inset: 0; z-index: 1001;
  display: flex; align-items: center; justify-content: center;
  box-sizing: border-box;
  padding: max(8px, env(safe-area-inset-top)) max(8px, env(safe-area-inset-right))
           max(8px, env(safe-area-inset-bottom)) max(8px, env(safe-area-inset-left));
  background:
    radial-gradient(ellipse 70% 62% at 50% 55%, rgba(0,0,0,0.55), transparent 100%),
    rgba(6,4,2,0.78);
}
.bv-home-scrim[hidden] { display: none; }

.bv-home-stage {
  position: relative; display: flex; flex-direction: column; align-items: center;
  width: min(100%, 860px); max-height: 100%; min-height: 0;
}

.bv-home {
  position: relative;
  width: 100%; max-height: 86vh;
  padding: 18px 20px 20px;
  overflow: hidden;
  display: flex; flex-direction: column;
}

.bv-home-body { display: flex; flex-direction: column; min-height: 0; flex: 1 1 auto; }

.bv-home-title {
  position: relative; z-index: 2; margin: 0 0 10px; padding: 0 8px; text-align: center;
  font: 800 clamp(1.3rem, 5vw, 1.9rem)/1.1 var(--font-display); letter-spacing: 0.08em;
  text-transform: uppercase; color: var(--brass-hot);
  text-shadow: 0 2px 0 rgba(0,0,0,0.7), 0 0 18px rgba(255,189,92,0.35), 0 4px 14px rgba(0,0,0,0.8);
}
.bv-home-flag {
  font: 10px/1.4 var(--font-display); text-transform: uppercase; letter-spacing: 0.08em;
  color: var(--warn); border: 1px solid var(--warn); border-radius: var(--radius-tape);
  padding: 1px 6px;
}

/* ─── Tabs ────────────────────────────────────────────────────────────── */
.bv-home-tabs { display: flex; gap: 4px; margin: 10px 0 0; border-bottom: 1px solid var(--line); }
.bv-home-tab {
  background: none; border: 0; border-bottom: 2px solid transparent; cursor: pointer;
  padding: 6px 12px 7px;
  font: 600 12px/1.2 var(--font-display); text-transform: uppercase; letter-spacing: 0.08em;
  color: var(--bone-dim);
}
.bv-home-tab:hover { color: var(--bone); }
.bv-home-tab-on { color: var(--brass-bright); border-bottom-color: var(--brass); }

.bv-home-tabbody { overflow-y: auto; padding: 12px 2px 2px; min-height: 0; flex: 1 1 auto; }

.bv-home-error, .bv-home-notice {
  margin-top: 8px; padding: 6px 10px; border-radius: var(--radius-well);
  font: 12px/1.4 var(--font-body);
}
.bv-home-error { color: var(--warn); border: 1px solid var(--warn); background: rgba(0,0,0,0.25); }
.bv-home-notice { color: var(--moss); border: 1px solid var(--moss-dark); background: rgba(0,0,0,0.25); }

.bv-home-empty { color: var(--bone-faint); font: 13px/1.5 var(--font-body); padding: 24px 0; text-align: center; }
.bv-home-note { color: var(--bone-dim); font: 12px/1.5 var(--font-note); margin: 4px 0; }
.bv-home-note-short { color: var(--warn); }
.bv-home-lead { color: var(--bone-dim); font: 13px/1.5 var(--font-body); margin-bottom: 12px; }
.bv-home-lead-locked { color: var(--warn); }

.bv-home-h3 {
  font: 600 13px/1.2 var(--font-display); text-transform: uppercase; letter-spacing: 0.07em;
  color: var(--brass); margin-bottom: 8px;
}
.bv-home-h4 {
  font: 600 11px/1.2 var(--font-display); text-transform: uppercase; letter-spacing: 0.07em;
  color: var(--bone-dim); margin: 10px 0 4px;
}
.bv-home-h4-short { color: var(--warn); }
.bv-home-h4-planned { color: var(--bone-faint); }

/* ─── The House tab ───────────────────────────────────────────────────── */
.bv-home-house { display: grid; grid-template-columns: 170px 1fr; gap: 16px; align-items: start; }
@media (max-width: 640px) { .bv-home-house { grid-template-columns: 1fr; } }

/* position:relative is LOAD-BEARING. .bv-cp-canvas is absolute inset:0, so it
   sizes against its nearest positioned ancestor — an unpositioned host lets
   the survivor escape and render at full viewport size across the middle of
   the screen, which is exactly what the arena confirm modal did. */
.bv-home-preview-slot { width: 170px; height: 240px; }
.bv-home-preview {
  position: relative; touch-action: none;
  width: 170px; height: 240px;
  border: 1px solid var(--line); border-radius: var(--radius-well);
  background: radial-gradient(circle at 50% 30%, rgba(210,164,86,0.09), rgba(0,0,0,0.35));
  overflow: hidden;
}

.bv-home-bill { min-width: 0; }
.bv-home-bill-group { margin-bottom: 6px; }

.bv-home-mat, .bv-home-gate {
  display: grid; grid-template-columns: 28px 1fr auto; gap: 8px; align-items: center;
  padding: 3px 6px; border-radius: var(--radius-sm);
}
.bv-home-gate { grid-template-columns: 1fr auto; }
.bv-home-mat:nth-child(even), .bv-home-gate:nth-child(even) { background: rgba(0,0,0,0.18); }
.bv-home-mat-art { width: 28px; height: 28px; display: flex; align-items: center; justify-content: center; }
.bv-home-mat-icon { max-width: 28px; max-height: 28px; }
.bv-home-mat-name, .bv-home-gate-name { font: 13px/1.3 var(--font-body); color: var(--bone); }
.bv-home-mat-count, .bv-home-gate-count { font: 12px/1.3 var(--font-technical); color: var(--bone-dim); }
.bv-home-mat-short .bv-home-mat-count { color: var(--warn); }
.bv-home-mat-have .bv-home-mat-count { color: var(--moss); }
.bv-home-gate-met .bv-home-gate-count { color: var(--moss); }
.bv-home-gate:not(.bv-home-gate-met) .bv-home-gate-count { color: var(--warn); }

/* The horizontal span under the two columns: Home, N lvl -> next N+1 lvl. */
.bv-home-span {
  display: flex; align-items: center; justify-content: center; gap: 12px;
  margin-top: 14px; padding: 8px 12px;
  border-top: 1px solid var(--line); border-bottom: 1px solid var(--line);
  background: rgba(0,0,0,0.22);
}
.bv-home-span-now, .bv-home-span-next {
  font: 700 15px/1.1 var(--font-display); text-transform: uppercase; letter-spacing: 0.05em;
}
.bv-home-span-now { color: var(--bone); }
.bv-home-span-next { color: var(--brass-hot); }
.bv-home-span-arrow { color: var(--brass); font-size: 18px; }
.bv-home-span-cap { color: var(--bone-faint); font: 12px/1.2 var(--font-note); }

.bv-home-actions { display: flex; align-items: center; justify-content: center; gap: 10px; margin-top: 12px; }

.bv-home-upgrade {
  padding: 10px 28px; cursor: pointer;
  background: linear-gradient(160deg, var(--moss), var(--moss-dark));
  color: var(--paper); border: 1px solid var(--moss-dark); border-radius: var(--radius-sm);
  font: 700 13px/1.2 var(--font-display); text-transform: uppercase; letter-spacing: 0.08em;
}
.bv-home-upgrade:hover:not(:disabled) { filter: brightness(1.12); }
.bv-home-upgrade:disabled { opacity: 0.42; cursor: not-allowed; filter: grayscale(0.6); }
.bv-home-upgrade[hidden] { display: none; }

/* ─── Trade: the chooser ──────────────────────────────────────────────── */
.bv-home-specs {
  display: grid; grid-template-columns: repeat(auto-fit, minmax(140px, 1fr)); gap: 10px;
}
.bv-home-spec {
  position: relative; cursor: pointer; text-align: center;
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  padding: 14px 10px 12px;
  background: rgba(0,0,0,0.26); color: var(--bone);
  border: 1px solid var(--line); border-radius: var(--radius-well);
  transition: border-color 120ms ease, transform 120ms ease;
}
.bv-home-spec:hover { border-color: var(--brass); transform: translateY(-2px); }
.bv-home-spec[data-muted="1"] { opacity: 0.55; }
.bv-home-spec-on { border-color: var(--brass-hot); background: rgba(210,164,86,0.1); }

/* The icon SQUARE the brief asks for, with the title under it. */
.bv-home-spec-icon {
  width: 52px; height: 52px; display: flex; align-items: center; justify-content: center;
  border: 1px solid var(--line); border-radius: var(--radius-sm);
  background: rgba(0,0,0,0.35); color: var(--brass-bright);
}
.bv-home-spec-icon svg { width: 30px; height: 30px; }
.bv-home-spec-name {
  font: 700 13px/1.2 var(--font-display); text-transform: uppercase; letter-spacing: 0.04em;
  color: var(--bone);
}
.bv-home-spec-tag { font: 11px/1.35 var(--font-note); color: var(--bone-dim); }
.bv-home-spec-count { font: 10px/1.3 var(--font-technical); color: var(--bone-faint); }
.bv-home-spec-badge {
  position: absolute; top: 6px; right: 6px;
  font: 9px/1.3 var(--font-display); text-transform: uppercase; letter-spacing: 0.08em;
  color: var(--brass-hot); border: 1px solid var(--brass); border-radius: var(--radius-tape);
  padding: 1px 5px;
}

.bv-home-shared { margin-top: 12px; font: 12px/1.5 var(--font-body); color: var(--bone-dim); }
.bv-home-shared-label {
  font: 600 10px/1.2 var(--font-display); text-transform: uppercase; letter-spacing: 0.08em;
  color: var(--brass); margin-right: 6px;
}

.bv-home-blurb { font: 13px/1.6 var(--font-body); color: var(--bone); margin-bottom: 6px; }
.bv-home-crafts { list-style: none; margin: 0; padding: 0; }
.bv-home-craft { display: flex; justify-content: space-between; gap: 10px; padding: 3px 0; font: 13px/1.4 var(--font-body); }
.bv-home-craft-name { color: var(--bone); }
.bv-home-craft-gate { font: 11px/1.4 var(--font-technical); color: var(--moss); }
.bv-home-craft-locked .bv-home-craft-name { color: var(--bone-faint); }
.bv-home-craft-locked .bv-home-craft-gate { color: var(--rust); }
.bv-home-craft-none { color: var(--bone-faint); font-style: italic; }

.bv-home-perk-group { display: flex; gap: 10px; margin: 4px 0; }
.bv-home-perk-kind {
  flex: none; width: 66px;
  font: 600 10px/1.6 var(--font-display); text-transform: uppercase; letter-spacing: 0.06em;
  color: var(--bone-faint);
}
.bv-home-perk-list { list-style: none; margin: 0; padding: 0; font: 12px/1.6 var(--font-body); color: var(--bone-dim); }

.bv-home-detail-foot { display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px; align-items: center; }
.bv-home-detail-note { font: 12px/1.4 var(--font-note); color: var(--moss); margin-right: auto; }
.bv-home-commit {
  padding: 9px 20px; cursor: pointer;
  background: linear-gradient(160deg, var(--brass), var(--rust));
  color: var(--paper); border: 1px solid var(--brass); border-radius: var(--radius-sm);
  font: 700 12px/1.2 var(--font-display); text-transform: uppercase; letter-spacing: 0.07em;
}
.bv-home-commit:hover:not(:disabled) { filter: brightness(1.12); }
.bv-home-commit:disabled { opacity: 0.42; cursor: not-allowed; filter: grayscale(0.6); }

/* ─── Workshop ────────────────────────────────────────────────────────── */
.bv-home-bench-head {
  display: flex; justify-content: space-between; align-items: baseline;
  padding-bottom: 8px; border-bottom: 1px solid var(--line);
  font: 11px/1.3 var(--font-technical); color: var(--bone-dim);
}
.bv-home-bench-level { color: var(--brass); }

.bv-home-jobs { margin: 8px 0 12px; }
.bv-home-job {
  display: grid; grid-template-columns: 1fr auto auto; gap: 10px; align-items: center;
  padding: 6px 8px; border-radius: var(--radius-sm); background: rgba(0,0,0,0.24);
  margin-bottom: 4px;
}
.bv-home-job-ready { background: rgba(122,138,90,0.16); }
.bv-home-job-name { font: 13px/1.3 var(--font-body); color: var(--bone); }
.bv-home-job-time { font: 12px/1.3 var(--font-technical); color: var(--bone-dim); }
.bv-home-job-ready .bv-home-job-time { color: var(--moss); }

.bv-home-recipe {
  display: flex; justify-content: space-between; align-items: center; gap: 12px;
  padding: 8px; border-radius: var(--radius-sm); border: 1px solid transparent;
}
.bv-home-recipe:nth-child(odd) { background: rgba(0,0,0,0.18); }
.bv-home-recipe-locked { opacity: 0.6; border-color: var(--line); }
.bv-home-recipe-main { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
.bv-home-recipe-name { font: 600 13px/1.3 var(--font-body); color: var(--bone); }
.bv-home-recipe-inputs { font: 11px/1.3 var(--font-technical); color: var(--bone-faint); }
.bv-home-recipe-status { font: 11px/1.3 var(--font-technical); color: var(--warn); }
.bv-home-recipe-locked .bv-home-recipe-status { color: var(--rust); }
.bv-home-recipe-side { display: flex; align-items: center; gap: 10px; flex: none; }
.bv-home-recipe-time { font: 11px/1.3 var(--font-technical); color: var(--bone-dim); }

/* The Gold shortcut past the Overseer's quest. Brass rather than moss: it
   spends the premium currency, and the upgrade button beside it does not. */
.bv-home-key {
  margin: 8px 0 4px; padding: 8px 16px; cursor: pointer;
  background: linear-gradient(160deg, var(--brass), var(--rust));
  color: var(--paper); border: 1px solid var(--brass); border-radius: var(--radius-sm);
  font: 700 11px/1.2 var(--font-display); text-transform: uppercase; letter-spacing: 0.07em;
}
.bv-home-key:hover:not(:disabled) { filter: brightness(1.12); }
.bv-home-key:disabled { opacity: 0.42; cursor: not-allowed; filter: grayscale(0.6); }
.bv-home-key-note { display: block; font: 11px/1.4 var(--font-note); color: var(--warn); }

.bv-home-skip { color: var(--brass-bright); border-color: var(--brass); }

/* ─── Buttons + modal ─────────────────────────────────────────────────── */
.bv-home-btn {
  padding: 7px 14px; cursor: pointer;
  background: rgba(0,0,0,0.32); color: var(--bone);
  border: 1px solid var(--line); border-radius: var(--radius-sm);
  font: 600 11px/1.2 var(--font-display); text-transform: uppercase; letter-spacing: 0.07em;
}
.bv-home-btn:hover:not(:disabled) { color: var(--brass-bright); border-color: var(--brass); }
.bv-home-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.bv-home-btn-go { background: linear-gradient(160deg, var(--moss), var(--moss-dark)); color: var(--paper); border-color: var(--moss-dark); }

/* Absolute inside .bv-home, NOT a second fixed full-screen layer: another
   fixed inset-0 would stack above .bv-hud on some paths, and the HUD staying
   readable over every modal is a property this client deliberately has. */
.bv-home-modal {
  position: absolute; inset: 0; z-index: 2;
  display: flex; align-items: center; justify-content: center;
  background: rgba(6,4,2,0.72); padding: 18px;
}
.bv-home-modal-card { width: min(100%, 460px); max-height: 100%; overflow-y: auto; padding: 16px 18px; }
.bv-home-modal-title {
  font: 700 1.1rem/1.2 var(--font-display); text-transform: uppercase; letter-spacing: 0.03em;
  color: var(--brass-hot); margin-bottom: 8px;
}
.bv-home-modal-body { font: 13px/1.5 var(--font-body); color: var(--bone); }
.bv-home-modal-foot { display: flex; justify-content: flex-end; gap: 8px; margin-top: 14px; }

.bv-home-cards { display: grid; grid-template-columns: repeat(auto-fill, minmax(96px, 1fr)); gap: 8px; margin-top: 6px; }
.bv-home-card {
  display: flex; flex-direction: column; align-items: center; gap: 4px;
  padding: 8px 6px; border: 1px solid var(--line); border-radius: var(--radius-well);
  background: rgba(0,0,0,0.28);
}
.bv-home-card-art { width: 48px; height: 48px; display: flex; align-items: center; justify-content: center; }
.bv-home-card-icon { max-width: 48px; max-height: 48px; }
.bv-home-card-name { font: 11px/1.3 var(--font-body); color: var(--bone); text-align: center; }
.bv-home-card-qty { font: 12px/1.2 var(--font-technical); color: var(--brass-bright); }

/* ═══ Wear and clutter (CLAUDE.md rule) ═══════════════════════════════════
   Recipe as the shop and the journal: worn plate face, grime and scratches,
   a hazard strip across the top, a sticker hanging off a corner, a soft
   shadow behind the stage. Everything decorative is pointer-events: none.
   Rules below use #home-ui so a later depth pass cannot flatten them at equal
   specificity. Text sits on a 0.8+ dark wash, so bone and bone-dim stay above
   4.5:1; the faint and rust tokens are lifted where they carry real text. */
.bv-home-stage::before {
  content: ''; position: absolute; z-index: 0; pointer-events: none;
  inset: 24px -40px -44px;
  background: radial-gradient(closest-side, rgba(0,0,0,0.62), rgba(0,0,0,0.3) 55%, transparent 100%);
  filter: blur(6px);
}
.bv-home-stage::after {
  content: ''; position: absolute; z-index: 3; pointer-events: none;
  bottom: -34px; left: -40px; width: 70px; height: 70px;
  background: url('/clutter/clutter-a.webp') center / contain no-repeat;
  transform: rotate(-8deg); filter: drop-shadow(0 3px 4px rgba(0,0,0,0.6));
}
#home-ui .bv-home-stage > .bv-home { position: relative; z-index: 1; }
#home-ui .bv-home {
  max-height: calc(100dvh - 5.2rem - env(safe-area-inset-top) - env(safe-area-inset-bottom));
  min-height: min(32rem, calc(100dvh - 5.2rem - env(safe-area-inset-top) - env(safe-area-inset-bottom)));
  overscroll-behavior: contain;
  padding: 26px clamp(10px, 3vw, 20px) clamp(10px, 2.5vw, 18px);
  border: 1px solid var(--brass); border-radius: var(--radius-panel);
  background:
    linear-gradient(rgba(14,9,5,0.74), rgba(14,9,5,0.86)),
    url('/clutter/scratches.webp') center / 100% 100% no-repeat,
    url('/clutter/grime.webp') 20% 30% / 420px 420px,
    url('/clutter/plate.webp') 0 0 / 420px 420px, #17110a;
  box-shadow: 0 0 0 1px #120d08, 0 14px 34px rgba(0,0,0,0.6), inset 0 0 26px rgba(0,0,0,0.55);
}
/* The hazard strip: a worn yellow-and-black band across the top edge. */
#home-ui .bv-home::before {
  content: ''; position: absolute; z-index: 1; pointer-events: none;
  left: 0; right: 0; top: 0; height: 10px;
  background: repeating-linear-gradient(-45deg, #c9951c 0 10px, #17110a 10px 20px);
  border-bottom: 1px solid var(--brass); opacity: 0.88;
}

/* The close cross is the shared .bv-close; here it only sits above the strip,
   the sticker and the title. */
#home-ui .bv-home .bv-home-close { top: 14px; right: 6px; }

/* Tabs: chip row, 44px targets. Leaves room for the close cross on the right. */
#home-ui .bv-home-tabs { margin: 0 52px 0 0; gap: 2px; border-bottom-color: var(--brass); flex: none; }
#home-ui .bv-home-tab {
  min-height: 44px; padding: 6px clamp(8px, 2.4vw, 16px); touch-action: manipulation;
  color: var(--bone-dim);
}
#home-ui .bv-home-tab:hover { color: var(--bone); }
#home-ui .bv-home-tab { white-space: nowrap; }
#home-ui .bv-home-tab-on { color: var(--brass-hot); border-bottom-color: var(--brass-hot); }
#home-ui .bv-home-tab:focus-visible { outline: 2px solid var(--brass-bright); outline-offset: -2px; }

/* The tab body is the one scroller: header, tabs and the flash lines keep
   their height, the body takes the rest and scrolls on its own. */
#home-ui .bv-home-tabbody {
  overflow-y: auto; overscroll-behavior: contain; padding: 12px 4px 4px;
  -webkit-overflow-scrolling: touch;
}
#home-ui .bv-home-error, #home-ui .bv-home-notice { flex: none; background: rgba(8,5,3,0.82); }
#home-ui .bv-home-notice { color: #a9c47c; }

/* Copy that was bone-faint or rust is real text on a texture: lift it. */
#home-ui .bv-home-empty, #home-ui .bv-home-span-cap, #home-ui .bv-home-spec-count,
#home-ui .bv-home-perk-kind, #home-ui .bv-home-h4-planned, #home-ui .bv-home-recipe-inputs,
#home-ui .bv-home-craft-locked .bv-home-craft-name, #home-ui .bv-home-craft-none { color: var(--bone-dim); }
#home-ui .bv-home-craft-locked .bv-home-craft-gate, #home-ui .bv-home-recipe-locked .bv-home-recipe-status { color: #e58a68; }
#home-ui .bv-home-mat-have .bv-home-mat-count, #home-ui .bv-home-gate-met .bv-home-gate-count,
#home-ui .bv-home-craft-gate, #home-ui .bv-home-job-ready .bv-home-job-time { color: #a9c47c; }
#home-ui .bv-home-gate:not(.bv-home-gate-met) .bv-home-gate-count, #home-ui .bv-home-mat-short .bv-home-mat-count { color: #e3a247; }

/* ── The House tab: sections as plates ───────────────────────────────── */
#home-ui .bv-home-house { grid-template-columns: clamp(120px, 24%, 170px) minmax(0, 1fr); gap: 12px; }
#home-ui .bv-home-preview-slot, #home-ui .bv-home-preview {
  width: 100%; height: auto; aspect-ratio: 17 / 24; max-height: 46dvh;
}
#home-ui .bv-home-preview { border-color: var(--brass); box-shadow: inset 0 0 18px rgba(0,0,0,0.6), 0 3px 8px rgba(0,0,0,0.45); }
#home-ui .bv-home-bill-group,
#home-ui .bv-home-span {
  border: 1px solid var(--brass); border-radius: var(--radius-well);
  background:
    linear-gradient(168deg, rgba(40,30,19,0.8), rgba(14,10,6,0.9)),
    url('/clutter/scratches.webp') center / 100% 100% no-repeat,
    url('/clutter/grime.webp') 30% 20% / 300px 300px,
    url('/clutter/plate.webp') 0 0 / 300px 300px, #17110a;
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.09), inset 0 0 14px rgba(0,0,0,0.5), 0 3px 8px rgba(0,0,0,0.45);
}
#home-ui .bv-home-bill-group { padding: 8px 10px 6px; margin-bottom: 8px; isolation: isolate; }
#home-ui .bv-home-h3 { color: var(--brass-bright); border-bottom: 1px solid var(--line); padding-bottom: 6px; }
#home-ui .bv-home-h4 { margin-top: 0; color: var(--brass-bright); }
#home-ui .bv-home-h4-short { color: #e3a247; }
#home-ui .bv-home-mat, #home-ui .bv-home-gate { min-height: 36px; }
#home-ui .bv-home-span { margin-top: 12px; padding: 10px 12px; border-left-width: 1px; border-right-width: 1px; }
#home-ui .bv-home-actions { flex-wrap: wrap; margin-top: 12px; }
#home-ui .bv-home-upgrade {
  min-height: 44px; min-width: 44px; touch-action: manipulation;
  border: 1px solid var(--brass); border-radius: var(--radius-sm);
}
#home-ui .bv-home-upgrade { border-color: var(--moss); }
#home-ui .bv-home-upgrade:focus-visible,
#home-ui .bv-home-btn:focus-visible, #home-ui .bv-home-commit:focus-visible,
#home-ui .bv-home-spec:focus-visible { outline: 2px solid var(--brass-bright); outline-offset: 2px; }

/* ── Trade: tiles as worn plates, uniform thin border ───────────────── */
#home-ui .bv-home-specs { grid-template-columns: repeat(auto-fit, minmax(min(100%, 150px), 1fr)); }
#home-ui .bv-home-spec {
  min-height: 44px; touch-action: manipulation; isolation: isolate;
  border: 1px solid var(--brass); border-radius: var(--radius-well); color: var(--bone);
  background:
    linear-gradient(168deg, rgba(40,30,19,0.8), rgba(14,10,6,0.9)),
    url('/clutter/scratches.webp') center / 100% 100% no-repeat,
    url('/clutter/grime.webp') 30% 20% / 300px 300px,
    url('/clutter/plate.webp') 0 0 / 300px 300px, #17110a;
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.09), inset 0 0 14px rgba(0,0,0,0.5), 0 3px 8px rgba(0,0,0,0.45);
}
#home-ui .bv-home-spec:hover { border-color: var(--brass-hot); }
#home-ui .bv-home-spec-on { border-color: var(--brass-hot); box-shadow: 0 0 0 1px var(--brass-hot), inset 0 0 14px rgba(0,0,0,0.5), 0 3px 8px rgba(0,0,0,0.45); }
#home-ui .bv-home-spec-icon { border-color: var(--brass); background: rgba(8,5,3,0.7); }
#home-ui .bv-home-spec-tag { color: var(--bone-dim); }
#home-ui .bv-home-spec-badge { background: rgba(8,5,3,0.9); }
#home-ui .bv-home-shared { background: rgba(8,5,3,0.55); padding: 8px 10px; border: 1px solid var(--line); border-radius: var(--radius-well); }

/* ── Workshop: bench head, jobs and recipes as plates ───────────────── */
#home-ui .bv-home-bench-head { color: var(--bone-dim); border-bottom-color: var(--brass); font-size: 12px; }
#home-ui .bv-home-job, #home-ui .bv-home-recipe {
  border: 1px solid var(--line); border-radius: var(--radius-well);
  background: linear-gradient(168deg, rgba(40,30,19,0.7), rgba(14,10,6,0.85)), url('/clutter/grime.webp') 30% 20% / 300px 300px, #17110a;
  margin-bottom: 6px;
}
#home-ui .bv-home-job-ready { border-color: var(--moss); }
#home-ui .bv-home-recipe:nth-child(odd) { background: linear-gradient(168deg, rgba(40,30,19,0.7), rgba(14,10,6,0.85)), url('/clutter/grime.webp') 30% 20% / 300px 300px, #17110a; }
#home-ui .bv-home-recipe-locked { opacity: 1; }
#home-ui .bv-home-recipe-locked .bv-home-recipe-name { color: var(--bone-dim); }
#home-ui .bv-home-btn, #home-ui .bv-home-commit, #home-ui .bv-home-key {
  min-height: 44px; min-width: 44px; touch-action: manipulation;
}
#home-ui .bv-home-btn { background: rgba(14,9,5,0.82); border-color: var(--brass); }
#home-ui .bv-home-btn-go { background: linear-gradient(160deg, var(--moss), var(--moss-dark)); border-color: var(--moss); }
#home-ui .bv-home-btn:hover:not(:disabled) { color: var(--brass-hot); border-color: var(--brass-hot); }
#home-ui .bv-home-btn-go:hover:not(:disabled) { color: var(--paper); }

/* ── Modal + cards: same plate language, uniform thin border ────────── */
#home-ui .bv-home-modal {
  z-index: 8; border-radius: inherit;
  padding: max(10px, 3%); background: rgba(6,4,2,0.78);
}
#home-ui .bv-home-modal-card {
  position: relative; isolation: isolate; width: min(100%, 460px);
  max-height: 100%; overscroll-behavior: contain;
  border: 1px solid var(--brass); border-radius: var(--radius-panel);
  background:
    linear-gradient(rgba(14,9,5,0.78), rgba(14,9,5,0.88)),
    url('/clutter/scratches.webp') center / 100% 100% no-repeat,
    url('/clutter/grime.webp') 20% 30% / 360px 360px,
    url('/clutter/plate.webp') 0 0 / 360px 360px, #17110a;
  box-shadow: 0 0 0 1px #120d08, 0 14px 34px rgba(0,0,0,0.7), inset 0 0 24px rgba(0,0,0,0.55);
}
#home-ui .bv-home-modal-title { color: var(--brass-hot); text-shadow: 0 2px 0 rgba(0,0,0,0.7); }
#home-ui .bv-home-modal-card { display: flex; flex-direction: column; overflow: hidden; }
#home-ui .bv-home-modal-body { flex: 1 1 auto; min-height: 0; overflow-y: auto; overscroll-behavior: contain; padding-right: 2px; }
#home-ui .bv-home-modal-title, #home-ui .bv-home-modal-foot, #home-ui .bv-home-detail-foot { flex: none; }
#home-ui .bv-home-modal-foot { flex-wrap: wrap; }
#home-ui .bv-home-spec-count { font-size: 11px; }
#home-ui .bv-home-upgrade:disabled { opacity: 0.6; background: rgba(14,9,5,0.82); border-color: var(--line); color: var(--bone-dim); }
#home-ui .bv-home-detail-foot { flex-wrap: wrap; }
#home-ui .bv-home-cards { grid-template-columns: repeat(auto-fill, minmax(88px, 1fr)); }
#home-ui .bv-home-card {
  isolation: isolate; border: 1px solid var(--brass); border-radius: var(--radius-well);
  background:
    linear-gradient(168deg, rgba(40,30,19,0.8), rgba(14,10,6,0.9)),
    url('/clutter/scratches.webp') center / 100% 100% no-repeat,
    url('/clutter/grime.webp') 30% 20% / 260px 260px,
    url('/clutter/plate.webp') 0 0 / 260px 260px, #17110a;
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.09), inset 0 0 12px rgba(0,0,0,0.5), 0 2px 6px rgba(0,0,0,0.45);
}
#home-ui .bv-home-card-name { color: var(--bone); }
#home-ui .bv-home-card-qty { color: var(--brass-hot); }

/* ── House tab: the bill as cards (the old Materials popup, inline) ─────
   The count climbs with hideout plus pack. At x / x the card is done: a moss
   fill over the plate, a check badge in the corner and a soft green glow.
   Short cards keep the amber count so the eye lands on what is missing. */
#home-ui .bv-home-bill .bv-home-cards { margin-top: 8px; }
#home-ui .bv-home-bill .bv-home-card { position: relative; padding: 10px 6px 8px; min-height: 44px; }
#home-ui .bv-home-bill .bv-home-card-name { min-height: 2.6em; display: flex; align-items: center; }
#home-ui .bv-home-bill .bv-home-card-qty { font-size: 13px; color: #e3a247; }
#home-ui .bv-home-card-pack { font: 10px/1.2 var(--font-technical); color: var(--bone-dim); text-align: center; }
#home-ui .bv-home-bill .bv-home-card-done {
  border-color: #8fb35a;
  background:
    linear-gradient(168deg, rgba(92,122,48,0.62), rgba(34,48,18,0.86)),
    url('/clutter/scratches.webp') center / 100% 100% no-repeat,
    url('/clutter/grime.webp') 30% 20% / 260px 260px,
    url('/clutter/plate.webp') 0 0 / 260px 260px, #17110a;
  box-shadow: 0 0 0 1px rgba(169,196,124,0.35), 0 0 14px rgba(150,200,90,0.45),
    inset 0 0 14px rgba(190,230,130,0.18), 0 2px 6px rgba(0,0,0,0.45);
}
#home-ui .bv-home-bill .bv-home-card-done .bv-home-card-qty { color: #cfe8a0; }
#home-ui .bv-home-bill .bv-home-card-done .bv-home-card-name { color: var(--paper); }
#home-ui .bv-home-card-check {
  position: absolute; top: -7px; right: -7px; z-index: 1;
  width: 24px; height: 24px; display: flex; align-items: center; justify-content: center;
  border-radius: 50%; border: 1px solid #cfe8a0;
  background: linear-gradient(160deg, var(--moss), var(--moss-dark)); color: var(--paper);
  box-shadow: 0 0 10px rgba(170,220,110,0.7), 0 2px 4px rgba(0,0,0,0.6);
}
#home-ui .bv-home-card-check svg { width: 15px; height: 15px; }
#home-ui .bv-home-h4-done { color: #a9c47c; }
#home-ui .bv-home-stash { text-align: center; margin-top: 8px; color: #e3a247; }
@media (prefers-reduced-motion: no-preference) {
  #home-ui .bv-home-bill .bv-home-card-done { animation: bv-home-card-glow 2.4s ease-in-out infinite; }
}
@keyframes bv-home-card-glow {
  0%, 100% { box-shadow: 0 0 0 1px rgba(169,196,124,0.35), 0 0 10px rgba(150,200,90,0.35), inset 0 0 14px rgba(190,230,130,0.14), 0 2px 6px rgba(0,0,0,0.45); }
  50% { box-shadow: 0 0 0 1px rgba(169,196,124,0.55), 0 0 18px rgba(150,200,90,0.6), inset 0 0 16px rgba(190,230,130,0.24), 0 2px 6px rgba(0,0,0,0.45); }
}
@media (max-width: 420px) {
  #home-ui .bv-home-bill .bv-home-cards { grid-template-columns: repeat(auto-fill, minmax(78px, 1fr)); gap: 10px 6px; }
}

/* ═══ Specialization chooser: painted emblems, plain counts, spaced text ═══
   The tile is a plate with the emblem on a recessed brass-rimmed well, then name,
   tagline and the two recipe lines as separate rows with their own spacing. The
   popup is the same plate language as the panel: hazard strip, grime, a hero
   emblem and the sticker. Class names only, no backticks in this block. */
#home-ui .bv-home-specs { gap: 10px; grid-template-columns: repeat(auto-fit, minmax(min(100%, 140px), 1fr)); }
#home-ui .bv-home-spec {
  gap: 0; padding: 16px 12px 14px; align-items: stretch; text-align: center; min-height: 0;
}
#home-ui .bv-home-spec-icon {
  align-self: center; width: 92px; height: 92px; margin-bottom: 10px;
  border: 1px solid var(--brass); border-radius: 50%;
  background: radial-gradient(circle at 50% 38%, rgba(210,164,86,0.2), rgba(8,5,3,0.85) 72%);
  box-shadow: inset 0 0 14px rgba(0,0,0,0.75), 0 0 0 3px rgba(8,5,3,0.7), 0 0 0 4px rgba(210,164,86,0.35);
}
#home-ui .bv-home-spec-art { width: 78%; height: 78%; object-fit: contain; filter: drop-shadow(0 2px 3px rgba(0,0,0,0.7)); }
#home-ui .bv-home-spec-icon svg { width: 44px; height: 44px; }
#home-ui .bv-home-spec:hover .bv-home-spec-art { transform: scale(1.06); }
.bv-home-spec-art { transition: transform 140ms ease; }
#home-ui .bv-home-spec-name {
  margin: 0 0 6px; padding-bottom: 7px; font-size: 14px; letter-spacing: 0.06em; color: var(--brass-hot);
  border-bottom: 1px solid rgba(210,164,86,0.35);
}
#home-ui .bv-home-spec-tag { margin: 0 0 10px; font: 12px/1.45 var(--font-note); color: var(--bone); min-height: 2.9em; text-wrap: balance; }
#home-ui .bv-home-spec-stats { display: flex; flex-direction: column; gap: 4px; margin-top: auto; }
#home-ui .bv-home-spec-stat {
  display: block; padding: 3px 6px; border-radius: var(--radius-sm);
  font: 600 11px/1.35 var(--font-technical); color: var(--bone-dim); background: rgba(8,5,3,0.55);
}
#home-ui .bv-home-spec-stat-ready { color: #a9c47c; }
#home-ui .bv-home-spec-stat-later { color: #e3a247; }
#home-ui .bv-home-spec-stat-none { color: var(--bone-dim); font-style: italic; }
#home-ui .bv-home-spec-badge { top: 8px; right: 8px; color: var(--brass-hot); }
#home-ui .bv-home-spec-badge-inline { position: static; display: inline-block; margin-top: 6px; }

#home-ui .bv-home-shared {
  display: flex; align-items: center; gap: 12px; margin-top: 14px; padding: 10px 12px;
  font: 13px/1.5 var(--font-body); color: var(--bone);
}
#home-ui .bv-home-shared-art { flex: none; width: 44px; height: 44px; object-fit: contain; filter: drop-shadow(0 2px 3px rgba(0,0,0,0.7)); }
#home-ui .bv-home-shared-text { margin: 0; min-width: 0; }
#home-ui .bv-home-shared-label {
  display: block; margin: 0 0 2px; color: var(--brass-bright);
  font: 600 11px/1.3 var(--font-display); letter-spacing: 0.08em; text-transform: uppercase;
}

/* The popup */
#home-ui .bv-home-modal-card-spec { width: min(100%, 540px); padding: 0; }
#home-ui .bv-home-modal-card-spec::before {
  content: ''; position: absolute; z-index: 1; pointer-events: none; left: 0; right: 0; top: 0; height: 9px;
  background: repeating-linear-gradient(-45deg, #c9951c 0 10px, #17110a 10px 20px);
  border-bottom: 1px solid var(--brass); opacity: 0.88;
}
#home-ui .bv-home-modal-card-spec::after {
  content: ''; position: absolute; z-index: 3; pointer-events: none; right: -16px; top: -22px;
  width: 54px; height: 54px; background: url('/clutter/clutter-b.webp') center / contain no-repeat;
  transform: rotate(9deg); filter: drop-shadow(0 3px 4px rgba(0,0,0,0.6));
}
#home-ui .bv-home-spec-head {
  display: flex; align-items: center; gap: 16px; flex: none;
  padding: 24px 20px 14px; border-bottom: 1px solid var(--brass);
  background: linear-gradient(180deg, rgba(210,164,86,0.1), transparent);
}
#home-ui .bv-home-spec-hero {
  flex: none; width: 96px; height: 96px; display: flex; align-items: center; justify-content: center;
  border: 1px solid var(--brass); border-radius: 50%;
  background: radial-gradient(circle at 50% 38%, rgba(210,164,86,0.22), rgba(8,5,3,0.88) 72%);
  box-shadow: inset 0 0 14px rgba(0,0,0,0.75), 0 0 0 3px rgba(8,5,3,0.7), 0 0 0 4px rgba(210,164,86,0.35);
}
#home-ui .bv-home-spec-hero .bv-home-spec-art { width: 80%; height: 80%; }
#home-ui .bv-home-spec-headtext { min-width: 0; }
#home-ui .bv-home-spec-head .bv-home-modal-title { margin: 0 0 4px; font-size: clamp(1.25rem, 5vw, 1.6rem); letter-spacing: 0.06em; }
#home-ui .bv-home-spec-headtag { margin: 0; font: 14px/1.4 var(--font-note); color: var(--bone); }
#home-ui .bv-home-modal-card-spec .bv-home-modal-body { padding: 14px 20px 4px; }
#home-ui .bv-home-modal-card-spec .bv-home-blurb { margin: 0 0 14px; line-height: 1.65; }
#home-ui .bv-home-modal-card-spec .bv-home-h4 {
  margin: 16px 0 8px; padding-bottom: 5px; font-size: 12px; letter-spacing: 0.1em;
  border-bottom: 1px solid var(--line); color: var(--brass-bright);
}
#home-ui .bv-home-modal-card-spec .bv-home-h4-planned { color: var(--bone-dim); }
#home-ui .bv-home-crafts { display: flex; flex-direction: column; gap: 5px; }
#home-ui .bv-home-craft {
  align-items: center; padding: 7px 10px; border: 1px solid var(--line); border-radius: var(--radius-sm);
  background: rgba(8,5,3,0.55);
}
#home-ui .bv-home-craft-gate { display: inline-flex; align-items: center; gap: 6px; flex: none; font-size: 12px; white-space: nowrap; }
#home-ui .bv-home-craft-lock { width: 20px; height: 20px; object-fit: contain; }
#home-ui .bv-home-craft-locked { border-style: dashed; }
#home-ui .bv-home-perk-group {
  display: grid; grid-template-columns: 78px minmax(0, 1fr); gap: 12px; align-items: baseline;
  margin: 0; padding: 8px 0; border-bottom: 1px dotted rgba(210,164,86,0.25);
}
#home-ui .bv-home-perk-group:last-child { border-bottom: 0; }
#home-ui .bv-home-perk-kind { width: auto; font-size: 11px; line-height: 1.5; color: var(--brass); }
#home-ui .bv-home-perk-list { font: 13px/1.55 var(--font-body); color: var(--bone); }
#home-ui .bv-home-perk-list li + li { margin-top: 4px; }
#home-ui .bv-home-modal-card-spec .bv-home-note { margin: 12px 0 0; }
#home-ui .bv-home-modal-card-spec .bv-home-detail-foot {
  margin: 0; padding: 12px 20px 16px; border-top: 1px solid var(--brass);
  background: rgba(8,5,3,0.55); position: relative; z-index: 2;
}
#home-ui .bv-home-commit { padding: 10px 22px; box-shadow: inset 0 1px 0 rgba(255,222,170,0.3), 0 2px 6px rgba(0,0,0,0.5); }
@media (max-width: 520px) {
  #home-ui .bv-home-spec-hero { width: 72px; height: 72px; }
  #home-ui .bv-home-spec-head { gap: 12px; padding: 22px 14px 12px; }
  #home-ui .bv-home-modal-card-spec .bv-home-modal-body { padding: 12px 14px 4px; }
  #home-ui .bv-home-modal-card-spec .bv-home-detail-foot { padding: 10px 14px 14px; }
  #home-ui .bv-home-perk-group { grid-template-columns: 1fr; gap: 2px; }
  #home-ui .bv-home-spec-icon { width: 76px; height: 76px; }
  #home-ui .bv-home-spec-tag { min-height: 0; }
  #home-ui .bv-home-modal-card-spec::after { display: none; }
}
@media (prefers-reduced-motion: reduce) { .bv-home-spec-art { transition: none; } #home-ui .bv-home-spec:hover .bv-home-spec-art { transform: none; } }

/* The chosen specialization as the Workshop header */
#home-ui .bv-home-mine {
  display: flex; gap: 16px; align-items: flex-start; margin-bottom: 14px; padding: 14px 16px;
  border: 1px solid var(--brass); border-radius: var(--radius-well);
  background:
    linear-gradient(168deg, rgba(40,30,19,0.8), rgba(14,10,6,0.9)),
    url('/clutter/scratches.webp') center / 100% 100% no-repeat,
    url('/clutter/grime.webp') 30% 20% / 300px 300px,
    url('/clutter/plate.webp') 0 0 / 300px 300px, #17110a;
  box-shadow: inset 0 1px 0 rgba(255,222,170,0.09), inset 0 0 14px rgba(0,0,0,0.5), 0 3px 8px rgba(0,0,0,0.45);
}
#home-ui .bv-home-mine-main { flex: 1 1 auto; min-width: 0; }
#home-ui .bv-home-mine-top { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; }
#home-ui .bv-home-mine-name {
  margin: 0 0 3px; font: 800 clamp(1.2rem, 4vw, 1.5rem)/1.1 var(--font-display); letter-spacing: 0.06em;
  text-transform: uppercase; color: var(--brass-hot); text-shadow: 0 2px 0 rgba(0,0,0,0.7);
}
#home-ui .bv-home-boosts { margin-top: 10px; padding-top: 8px; border-top: 1px solid rgba(210,164,86,0.35); }
#home-ui .bv-home-boost {
  display: grid; grid-template-columns: 74px minmax(0, 1fr); gap: 10px; align-items: baseline; padding: 3px 0;
}
#home-ui .bv-home-boost-kind { font: 600 11px/1.5 var(--font-display); text-transform: uppercase; letter-spacing: 0.06em; color: var(--brass); }
#home-ui .bv-home-boost-text { font: 13px/1.5 var(--font-body); color: var(--bone); }
#home-ui .bv-home-boost-note { margin: 6px 0 0; font: 12px/1.4 var(--font-note); color: var(--bone-dim); }
#home-ui .bv-home-back { margin-bottom: 10px; }
#home-ui .bv-home-bench-head { margin-top: 2px; }
@media (max-width: 520px) {
  #home-ui .bv-home-mine { gap: 10px; padding: 12px; }
  #home-ui .bv-home-boost { grid-template-columns: 1fr; gap: 0; }
}

/* ── Phones and short landscape ─────────────────────────────────────── */
@media (max-width: 520px) {
  #home-ui .bv-home-house { grid-template-columns: 1fr; }
  #home-ui .bv-home-preview-slot, #home-ui .bv-home-preview { width: min(150px, 100%); margin: 0 auto; max-height: 34dvh; }
  .bv-home-stage::before { inset: 20px -16px -20px; }
  .bv-home-stage::after { width: 40px; height: 40px; left: -4px; bottom: -26px; }
  #home-ui .bv-home {
    background:
      linear-gradient(rgba(14,9,5,0.78), rgba(14,9,5,0.88)),
      url('/clutter/grime.webp') 20% 30% / 260px 260px,
      url('/clutter/plate.webp') 0 0 / 260px 260px, #17110a;
  }
  #home-ui .bv-home-recipe { flex-wrap: nowrap; }
  #home-ui .bv-home-tabs { margin-right: 46px; }
  #home-ui .bv-home-tab { padding: 6px 7px; font-size: 11px; letter-spacing: 0.04em; }
  #home-ui .bv-home-recipe-main { flex: 1 1 auto; }
  #home-ui .bv-home-recipe-side { flex-direction: column; align-items: flex-end; gap: 4px; }
  #home-ui .bv-home-job { grid-template-columns: 1fr auto; }
  #home-ui .bv-home-job .bv-home-btn { grid-column: 1 / -1; }
}
@media (max-height: 520px) {
  #home-ui .bv-home { max-height: calc(100dvh - 3.6rem - env(safe-area-inset-top) - env(safe-area-inset-bottom)); min-height: min(32rem, calc(100dvh - 3.6rem - env(safe-area-inset-top) - env(safe-area-inset-bottom))); }
  .bv-home-title { margin-bottom: 4px; font-size: 1.15rem; }
  #home-ui .bv-home-preview-slot, #home-ui .bv-home-preview { max-height: 56dvh; }
}
@media (prefers-reduced-motion: reduce) {
  #home-ui .bv-home-spec, #home-ui .bv-home-spec:hover { transition: none; transform: none; }
}
`;
